import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent, useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import { regrasFiscaisBreadcrumbItems } from "data/regraFiscal";
import RegraFiscalTable, {
  regraFiscalTableColumns,
} from "components/tables/RegraFiscalTable";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import { usePermissionModal } from "contexts/PermissionModalContext";
import ButtonRevisar from "./button-revisar";
import ButtonAtualizar from "./button-atualizar";

const fetchRegrasFiscais = async ({
  pageIndex,
  pageSize,
  filter,
}: PaginationState & { filter?: string }) => {
  try {
    const { data } = await axios.get("/api/regraFiscal", {
      params: {
        pageIndex,
        pageSize,
        filter,
      },
    });
    return data;
  } catch (error) {
    // Allow the error to propagate to the query's error handler
    throw error;
  }
};

const RegrasFiscais = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [filter, setFilter] = useState<string | undefined>();
  const [isDataPermissionDenied, setIsDataPermissionDenied] = useState(false);
  const { showPermissionModal, getQueryRetryHandler, getMutationErrorHandler } = usePermissionModal();

  const dataQuery = useQuery({
    queryKey: ["regrasFiscais", { ...pagination, filter }],
    queryFn: () => fetchRegrasFiscais({ ...pagination, filter }),
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: ["regrasFiscais", { ...pagination, filter }],
      defaultMessage: "Você não possui permissão para consultar regras fiscais."
    }),
  });


  // Handle permission errors on first load without showing modal
  useEffect(() => {
    if (dataQuery.error && axios.isAxiosError(dataQuery.error) && dataQuery.error.response?.status === 403) {
      setIsDataPermissionDenied(true);
    }
  }, [dataQuery.error]);


  const table = useReactTable({
    data: dataQuery.data?.itens ?? [],
    columns: regraFiscalTableColumns,
    enableSorting: false,
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setFilter,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    pageCount: dataQuery.data?.totalPages ?? 0,
    state: {
      globalFilter: "",
      pagination: {
        pageIndex: dataQuery.data?.pageIndex ?? 0,
        pageSize: dataQuery.data?.pageSize ?? 10,
      },
    },
    initialState: {
      pagination: {
        pageIndex: dataQuery.data?.pageIndex ?? 0,
        pageSize: dataQuery.data?.pageSize ?? 10,
      },
    },
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={regrasFiscaisBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">Regras Fiscais</h2>

        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Pesquisar regra fiscal"
              onChange={handleSearchInputChange}
              disabled={isDataPermissionDenied}
            />
            <div className="ms-xxl-auto d-flex gap-2">
              <ButtonRevisar />
              <ButtonAtualizar />
              <Button
                variant="primary"
                as={Link}
                to="/cadastros/regras-fiscais/novo"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Cadastrar
              </Button>
            </div>
          </div>
        </div>

        {isDataPermissionDenied ? (
          <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar regras fiscais.">
            <div
              className="position-relative"
              style={{
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descrição</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>Regra Fiscal Exemplo</td>
                            <td>-</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </AccessDeniedOverlay>
        ) : (
          <AdvanceTableProvider {...table}>
            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
              <RegraFiscalTable />
            </div>
          </AdvanceTableProvider>
        )}
      </div>
    </div>
  );
};

export default RegrasFiscais;
